import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';

import Layout from '../components/Layout';
import theme from '../styles/theme';
import { recursosAPI } from '../services/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.xl};
  border: ${({ $variant }) =>
    $variant === 'secondary' ? `1px solid ${theme.colors.border.primary}` : 'none'};
  background: ${({ $variant }) =>
    $variant === 'primary' ? theme.colors.primary[500] : 'transparent'};
  color: ${({ $variant }) =>
    $variant === 'primary' ? theme.colors.text.primary : theme.colors.text.secondary};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $variant }) =>
      $variant === 'primary' ? theme.colors.primary[600] : theme.colors.primary[500]}15;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  max-width: 720px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  resize: vertical;
  min-height: 140px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const HelperText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid
    ${({ $type }) =>
      $type === 'error'
        ? theme.colors.error[500]
        : $type === 'success'
        ? theme.colors.success[500]
        : theme.colors.warning[500]};
  background: ${({ $type }) =>
    $type === 'error'
      ? theme.colors.error[100]
      : $type === 'success'
      ? theme.colors.success[100]
      : theme.colors.warning[100]};
  color: ${({ $type }) =>
    $type === 'error'
      ? theme.colors.error[700]
      : $type === 'success'
      ? theme.colors.success[700]
      : theme.colors.warning[700]};
  font-size: ${theme.fontSizes.sm};
`;

const RecursoForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const recursoId = params.id ? Number.parseInt(params.id, 10) : null;

  const [form, setForm] = useState({
    codigo: '',
    descricao: '',
  });
  const [loading, setLoading] = useState(!!recursoId);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [mensagem, setMensagem] = useState(null);

  useEffect(() => {
    const carregarRecurso = async () => {
      if (!recursoId) {
        setLoading(false);
        return;
      }

      try {
        const response = await recursosAPI.buscarPorId(recursoId);
        if (response.data?.success) {
          const dados = response.data.data;
          setForm({
            codigo: dados.codigo || '',
            descricao: dados.descricao || '',
          });
        } else {
          setErro('Recurso não encontrado.');
        }
      } catch (error) {
        console.error('Erro ao carregar recurso', error);
        setErro('Erro ao carregar recurso.');
      } finally {
        setLoading(false);
      }
    };

    carregarRecurso();
  }, [recursoId]);

  const handleChange = (campo, valor) => {
    setForm((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const formularioValido =
    form.codigo.trim().length > 0 && form.descricao.trim().length > 0 && form.codigo.length <= 100;

  const handleSubmit = async () => {
    if (!formularioValido) {
      setErro('Preencha o código (até 100 caracteres) e a descrição do recurso.');
      return;
    }

    try {
      setErro(null);
      setMensagem(null);
      setSalvando(true);

      const payload = {
        codigo: form.codigo.trim(),
        descricao: form.descricao.trim(),
      };

      if (recursoId) {
        const response = await recursosAPI.atualizar(recursoId, payload);
        if (!response.data?.success) {
          throw new Error('Não foi possível atualizar o recurso.');
        }
        setMensagem('Recurso atualizado com sucesso.');
      } else {
        const response = await recursosAPI.criar(payload);
        if (!response.data?.success) {
          throw new Error('Não foi possível cadastrar o recurso.');
        }
        navigate('/gestao/recursos');
        return;
      }
    } catch (error) {
      console.error('Erro ao salvar recurso', error);
      setErro(error.message || 'Erro ao salvar recurso.');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Carregando recurso">
        <Container>
          <Card>
            <Subtitle>Carregando dados do recurso...</Subtitle>
          </Card>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={recursoId ? 'Editar recurso' : 'Novo recurso'}>
      <Container>
        <Header>
          <TitleGroup>
            <Title>{recursoId ? 'Editar recurso' : 'Cadastrar novo recurso'}</Title>
            <Subtitle>
              Recursos representam funcionalidades que podem ser incluídas em planos comerciais.
            </Subtitle>
          </TitleGroup>
          <Actions>
            <Button $variant="secondary" onClick={() => navigate('/gestao/recursos')}>
              <FiArrowLeft />
              Voltar
            </Button>
            <Button $variant="primary" onClick={handleSubmit} disabled={salvando || !formularioValido}>
              <FiSave />
              {salvando ? 'Salvando...' : 'Salvar recurso'}
            </Button>
          </Actions>
        </Header>

        <Card>
          {erro && (
            <Alert $type="error">
              <FiAlertTriangle />
              {erro}
            </Alert>
          )}

          {mensagem && (
            <Alert $type="success">
              <FiCheckCircle />
              {mensagem}
            </Alert>
          )}

          <FormGroup>
            <Label>Código do recurso *</Label>
            <Input
              value={form.codigo}
              onChange={(e) => handleChange('codigo', e.target.value)}
              placeholder="Ex.: CHATBOT_CUSTOMIZADO"
            />
            <HelperText>Identificador único. Use letras maiúsculas, números e underscores.</HelperText>
          </FormGroup>

          <FormGroup>
            <Label>Descrição *</Label>
            <Textarea
              value={form.descricao}
              onChange={(e) => handleChange('descricao', e.target.value)}
              placeholder="Descreva o recurso e os benefícios oferecidos."
            />
          </FormGroup>
        </Card>
      </Container>
    </Layout>
  );
};

export default RecursoForm;


